import type { Environment } from "./environment";
import {
  createPersistedValue,
  type PersistedValueCodec,
} from "./persisted-value";
import { SHOPPER_ID_STORAGE_KEY } from "./shopper-identity";
import type { EventIdentifiers } from "./transport";

const ANONYMOUS_ID_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 2;

export type AnonymousIdStorage = "localStorage" | "cookies";

const idCodec: PersistedValueCodec<string> = {
  parse: (raw) => raw,
  serialize: (value) => value,
};

export interface AnonymousIdentity {
  resolve(): Promise<EventIdentifiers>;
  identify(identifiers?: EventIdentifiers): void;
}

export function createAnonymousIdentity(
  environment: Environment,
  enabled: boolean,
  backend: AnonymousIdStorage,
): AnonymousIdentity {
  const anonymousId = createPersistedValue(
    environment,
    {
      key: SHOPPER_ID_STORAGE_KEY,
      backend,
      cookieMaxAgeSeconds: ANONYMOUS_ID_COOKIE_MAX_AGE_SECONDS,
    },
    idCodec,
  );

  let identifiers: EventIdentifiers = {};

  // Serializes hydration before any minting so a resumed id wins over a fresh one.
  let pending: Promise<void> = enabled
    ? anonymousId.whenReady.then(() => {
        const resumed = anonymousId.get();
        if (resumed) {
          identifiers = { ...identifiers, anonymousId: resumed };
        }
      })
    : Promise.resolve();

  return {
    async resolve() {
      await pending;
      return identifiers;
    },
    identify(extra) {
      identifiers = { ...identifiers, ...extra };
      if (!enabled) {
        return;
      }
      pending = pending.then(() => {
        if (identifiers.anonymousId) {
          return;
        }
        const id = anonymousId.get() ?? environment.randomUUID();
        identifiers = { ...identifiers, anonymousId: id };
        anonymousId.set(id);
      });
    },
  };
}
